import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown, ChevronsRight, ChevronsLeft, Plus, X } from "lucide-react";
import type {
  Material, PricingContext, AllocationBasis, QuoteShipment,
} from "@/types";
import type { PricingMode } from "@/components/MaterialTable";
import { newQuoteShipment } from "@/services/api";
import type { LivePricedLine } from "@/services/api";
import { computeEngineMarginSummary } from "@/services/pricing";
import { formatPercent, formatRupees, moneyLocale } from "@/lib/format";
import { FlashValue } from "@/components/FlashValue";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows, overlayMaxWidth } from "@/styles/tokens";

const PANEL_WIDTH = layout.panelWidthAnalysis;
const RAIL_WIDTH  = layout.panelRailWidth;

const HEADING: React.CSSProperties = {
  fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wider,
};
const LABEL: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const VALUE: React.CSSProperties = {
  fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
  fontVariantNumeric: "tabular-nums",
};
const MUTED: React.CSSProperties = { fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder };

// ---------------------------------------------------------------------------

function SectionHeader({
  id, title, open, onToggle,
}: { id: string; title: string; open: boolean; onToggle: () => void }) {
  return (
    <button
      data-testid={`section-header-${id}`}
      onClick={onToggle}
      className="w-full flex items-center justify-between"
      style={{
        padding: `${spacing[10]}px ${spacing[16]}px`, background: colors.white,
        border: "none", borderBottom: `1px solid ${semanticColors.border}`,
        cursor: "pointer",
      }}
    >
      <span style={HEADING}>{title}</span>
      {open
        ? <ChevronDown size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
        : <ChevronRight size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />}
    </button>
  );
}

// ---------- Section 1: Sourcing Split -- REMOVED (app review P2-11) --------
//
// It split the lines by an origin that most lines do not carry and drove no
// number anywhere. Owner decision 2026-09-29: a control that does not save or
// drive a real number is not shown.

// ---------- Section 2: Freight, import mode --------------------------------
//
// ⚠️ THERE IS NO DOMESTIC FREIGHT SECTION, and its absence is the point. One
// used to render here on every non-import quote, reading the display blob in
// PricingContext.shipments: three mock shipments this app invented, a
// Rs 22,50,000 total, attached to lines by matching an ORIGIN STRING and read
// by nothing on the backend. On a quote marked domestic it stated a freight
// bill that did not exist. Deleted in BF5d-3 along with the allocator it fed.
//
// Domestic freight is the engine's `ibf_pct`, which this app sends as 0, so
// there is nothing to show and the section header is dropped rather than
// opening onto an empty panel.
//
// THIS one reads QuoteShipment[] — database records with real ids that
// actually price the lines.
//
// The basis list is shared with the backend's own enum: it is the choice the
// engine's allocator dispatches on, not a display option.
//
// NOTHING HERE ALLOCATES. A shipment states the booking and the bill; the
// split across lines is the engine's and arrives per line as freight_pct.

const BASIS_OPTIONS: AllocationBasis[] = ["By Weight", "By Volume", "By Value", "Equal Split"];
const BACKEND_MODES: QuoteShipment["mode"][] = ["sea", "air", "road"];

function ImportFreightSection({
  shipments, supplierCurrency, canEdit, onChangeShipments,
}: {
  shipments: QuoteShipment[];
  // The quote's currency, or empty when it has none recorded. Empty is what
  // disables Add: a shipment must state a bill in the currency the goods are
  // costed in, and the backend refuses one that disagrees.
  supplierCurrency: string;
  canEdit: boolean;
  onChangeShipments: (next: QuoteShipment[]) => void;
}) {
  const total = useMemo(
    () => shipments.reduce((acc, s) => acc + (s.freightTotal || 0), 0),
    [shipments],
  );

  // Keyed on `key`, never on array index: a delete re-indexes everything after
  // it, so an index-keyed patch would edit the wrong shipment the moment one
  // above it was removed.
  const update = (key: string, patch: Partial<QuoteShipment>) =>
    onChangeShipments(shipments.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const remove = (key: string) =>
    onChangeShipments(shipments.filter((s) => s.key !== key));

  const add = () => onChangeShipments([...shipments, newQuoteShipment(supplierCurrency)]);

  const canAdd = canEdit && !!supplierCurrency;

  return (
    <div style={{ padding: `${spacing[12]}px ${spacing[16]}px` }} data-testid="section-freight-import">
      {shipments.length === 0 && (
        <div style={{ ...MUTED, marginBottom: spacing[12] }} data-testid="shipments-empty">
          No shipments yet. Freight stays per line until one is added.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: spacing[12] }}>
        {shipments.map((s) => (
          <div key={s.key}
               data-testid={`quote-shipment-${s.key}`}
               style={{
                 border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                 padding: spacing[10], backgroundColor: semanticColors.panelBg,
                 display: "flex", flexDirection: "column", gap: spacing[8],
               }}>
            <div className="flex items-center justify-between" style={{ gap: spacing[8] }}>
              <input
                data-testid={`quote-shipment-name-${s.key}`}
                value={s.name}
                disabled={!canEdit}
                onChange={(e) => update(s.key, { name: e.target.value })}
                style={{
                  ...VALUE, fontWeight: typography.fontWeight.medium,
                  flex: 1, minWidth: 0, height: heights.iconButton,
                  padding: `0 ${spacing[6]}px`,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  backgroundColor: canEdit ? colors.white : "transparent",
                }}
              />
              {canEdit && (
                <button
                  data-testid={`quote-shipment-remove-${s.key}`}
                  onClick={() => remove(s.key)}
                  title="Remove this shipment"
                  style={{
                    width: heights.iconButtonSm, height: heights.iconButtonSm, padding: 0,
                    background: "transparent", border: "none", cursor: "pointer",
                    color: semanticColors.textSecondary, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <X size={iconSize.md} strokeWidth={iconStroke.regular} />
                </button>
              )}
            </div>

            {/* THE BILL. Typed in the quote's own supplier currency, which is
                shown as a static ISO code rather than a picker: the backend
                refuses a shipment whose currency disagrees with the quote's,
                so offering a choice would only ever offer a way to fail. */}
            <div className="flex items-center justify-between">
              <span style={LABEL}>Freight bill</span>
              <div className="flex items-center" style={{ gap: spacing[6] }}>
                <span style={MUTED} data-testid={`quote-shipment-currency-${s.key}`}>
                  {s.currency || "—"}
                </span>
                <input
                  data-testid={`quote-shipment-total-${s.key}`}
                  type="number"
                  min={0}
                  value={s.freightTotal}
                  disabled={!canEdit}
                  onChange={(e) => update(s.key, { freightTotal: Number(e.target.value) || 0 })}
                  style={{
                    ...VALUE, width: layout.shipmentBillWidth, height: heights.iconButton,
                    textAlign: "right", padding: `0 ${spacing[6]}px`,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    backgroundColor: canEdit ? colors.white : "transparent",
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span style={LABEL}>Mode</span>
              <select
                data-testid={`quote-shipment-mode-${s.key}`}
                value={s.mode}
                disabled={!canEdit}
                onChange={(e) => update(s.key, { mode: e.target.value as QuoteShipment["mode"] })}
                style={SHIPMENT_SELECT}
              >
                {BACKEND_MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between">
              <span style={LABEL}>Allocation basis</span>
              <select
                data-testid={`quote-shipment-basis-${s.key}`}
                value={s.allocationBasis}
                disabled={!canEdit}
                onChange={(e) => update(s.key, { allocationBasis: e.target.value as AllocationBasis })}
                style={SHIPMENT_SELECT}
              >
                {BASIS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* DISABLED, NOT HIDDEN, when the quote names no supplier currency. The
          bill has to be denominated in the currency the goods are costed in —
          that is what freight becomes a percentage of — so with none recorded
          there is nothing to denominate it in and the backend would refuse the
          whole save. A control that vanishes reads as a bug; one that is
          disabled and says why reads as a prerequisite. */}
      {canEdit && (
        <button
          data-testid="quote-shipment-add"
          onClick={add}
          disabled={!canAdd}
          title={canAdd
            ? "Add a shipment"
            : "Set the quote's supplier currency first — a freight bill must be denominated in the currency the goods are costed in."}
          style={{
            marginTop: spacing[12], width: "100%", height: heights.control,
            display: "flex", alignItems: "center", justifyContent: "center", gap: spacing[6],
            backgroundColor: colors.white,
            color: canAdd ? colors.indigo600 : semanticColors.textPlaceholder,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
            cursor: canAdd ? "pointer" : "not-allowed",
          }}
        >
          <Plus size={iconSize.md} strokeWidth={iconStroke.regular} />
          Add shipment
        </button>
      )}

      {/* A SUM OF THE BILLS, not an allocation — the same class of local
          aggregation the domestic total above already does. */}
      <div className="flex items-center justify-between"
           style={{ marginTop: spacing[12], paddingTop: spacing[10], borderTop: `1px solid ${semanticColors.border}` }}>
        <span style={LABEL}>Total freight billed</span>
        <FlashValue flashKey={total}
                    style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                             fontVariantNumeric: "tabular-nums" }}>
          <span data-testid="quote-shipment-total-billed">
            {supplierCurrency ? `${supplierCurrency} ` : ""}{total.toLocaleString(moneyLocale())}
          </span>
        </FlashValue>
      </div>
    </div>
  );
}

const SHIPMENT_SELECT: React.CSSProperties = {
  fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
  padding: `${spacing[2]}px ${spacing[6]}px`, height: heights.iconButton,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: colors.white, textTransform: "capitalize",
};

// ---------- Section 3: Customs — DELETED -----------------------------------
//
// ⚠️ IT SHOWED A DUTY BILL THAT DID NOT EXIST. computeCustoms selected lines
// by the same origin-string join the allocator used, so on a quote marked
// DOMESTIC -- where every row already carried freight 0 and duty 0, and the
// grid had stopped showing those columns at all -- this panel still charged
// BCD, SWS and IGST on an FX-adjusted assessable value. Measured on one
// 23 x Rs 7,128 line against the app's own default context: Assessable
// Rs 1,68,042.60, Total duty Rs 13,863.51. That is the d2b0892 bug, fixed in
// the grid and left standing in the panel.
//
// NOT REPLACED WITH AN ENGINE-BACKED ROLLUP IN THIS SLICE. Import duty is a
// per-line input (dutyPct) the import chain consumes, and aggregating the
// engine's own answer is a different piece of work from deleting a fabricated
// one. An empty section is honest; a placeholder kept for symmetry is not.

// ---------- Section 4: Conversion rate (import only, read-only) -----------
//
// The old section edited a placeholder customs rate, date and risk slider
// that no price ever read (app review P2-11). What is REAL is the rate each
// import line carries -- the one its price was worked out with -- so that is
// what is shown, read-only. A domestic quote has no section at all.

function ConversionRateSection({
  rows, supplierCurrency,
}: {
  rows: Material[];
  supplierCurrency: string;
}) {
  const rates = Array.from(new Set(
    rows.map((r) => r.conversionRate).filter((v): v is number => typeof v === "number" && v > 0),
  )).sort((a, b) => a - b);
  const pair = supplierCurrency ? `${supplierCurrency} → INR` : "Supplier currency not set";
  return (
    <div style={{ padding: `${spacing[12]}px ${spacing[16]}px` }} data-testid="section-fx">
      <div className="flex items-center justify-between">
        <span style={LABEL}>{pair}</span>
        <span data-testid="fx-line-rates" style={VALUE}>
          {rates.length === 0 ? "No rate on the lines yet" : rates.join(", ")}
        </span>
      </div>
      <div style={{ ...MUTED, marginTop: spacing[6] }}>
        The rate each line is priced at. Change it in the Conv. Rate column.
      </div>
    </div>
  );
}

// ---------- Section 5: Margin ----------------------------------------------

function MarginSection({
  rows, ctx, livePricing,
}: {
  rows: Material[];
  ctx: PricingContext;
  livePricing?: Map<number, LivePricedLine>;
}) {
  // ON DOMESTIC THE FIGURES COME FROM THE ENGINE, NOT THE PLACEHOLDERS.
  //
  // computeMarginSummary reads landedCost and lineTotal off the rows. Since
  // the domestic branch stopped inflating landedCost, those two no longer
  // describe the same thing -- lineTotal is a placeholder sell price while
  // landedCost is a real net price -- so the gross margin could read large and
  // NEGATIVE on a healthy quote. Summing the engine's own extended_sp and
  // cost_with_sv is aggregation of validated numbers, and it is the truth.
  //
  // null means "not priced yet", which the panel says outright rather than
  // showing a confident set of zeros.
  // ⚠️ THE NEGOTIATION BUFFER IS NOT APPLIED (app review P2-11): it was an
  // input that no price or document ever read, so it is gone from the panel
  // and the figures below are the engine's alone.
  const m = useMemo(
    () => computeEngineMarginSummary(rows, { ...ctx, negotiationBuffer: 0 }, livePricing ?? new Map()),
    [rows, ctx, livePricing],
  );

  // NOT PRICED YET -- say so instead of showing zeros. A panel of zeros claims
  // a quote is worth nothing; this says the number does not exist yet, which
  // is what is true until Update prices or a reopen supplies engine figures.
  if (!m) {
    return (
      <div style={{ padding: `${spacing[12]}px ${spacing[16]}px` }} data-testid="section-margin">
        <div style={MUTED} data-testid="margin-not-priced">
          Not priced yet — press Update prices to see margin.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: `${spacing[12]}px ${spacing[16]}px` }} data-testid="section-margin">
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
        <div className="flex items-center justify-between">
          <span style={LABEL}>Total Landed Cost</span>
          <FlashValue flashKey={m.totalLanded.toFixed(2)}
                      style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                               fontVariantNumeric: "tabular-nums" }}>
            <span data-testid="margin-landed">{formatRupees(m.totalLanded)}</span>
          </FlashValue>
        </div>
        <div className="flex items-center justify-between">
          <span style={LABEL}>Margin</span>
          <FlashValue flashKey={m.grossMargin.toFixed(2)} style={VALUE}>
            <span data-testid="margin-gross">{formatRupees(m.grossMargin)}</span>
          </FlashValue>
        </div>
      </div>

      <div style={{ marginTop: spacing[12], paddingTop: spacing[12], borderTop: `1px solid ${semanticColors.border}`,
                    display: "flex", flexDirection: "column", gap: spacing[6] }}>
        <div className="flex items-baseline justify-between">
          <span style={{ ...LABEL, color: semanticColors.textPrimary }}>Final Quote Value</span>
          <FlashValue flashKey={m.finalQuote.toFixed(2)}
                      style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                               fontVariantNumeric: "tabular-nums" }}>
            <span data-testid="margin-final-value">{formatRupees(m.finalQuote)}</span>
          </FlashValue>
        </div>
        <div className="flex items-baseline justify-between">
          <span style={{ ...LABEL, color: semanticColors.textPrimary }}>Realised Margin</span>
          <FlashValue flashKey={m.realisedMarginPct.toFixed(2)}
                      style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: colors.green600,
                               fontVariantNumeric: "tabular-nums" }}>
            <span data-testid="margin-realised">{m.realisedMarginPct.toFixed(1)}%</span>
          </FlashValue>
        </div>
      </div>
    </div>
  );
}

// ---------- Panel shell ----------------------------------------------------

export function SidePanel({
  rows, ctx, collapsed, onToggle, onGeneratePdf,
  pricingMode = "domestic",
  shipments = [],
  supplierCurrency = "",
  canEdit = true,
  livePricing,
  onChangeShipments,
  stacked = false,
}: {
  rows: Material[];
  ctx: PricingContext;
  // Kept for callers; the panel no longer edits the pricing context (its
  // placeholder rate and buffer inputs were removed, app review P2-11).
  onChangeCtx?: (updater: (c: PricingContext) => PricingContext) => void;
  collapsed: boolean;
  onToggle: () => void;
  onGeneratePdf: () => void;
  // WHETHER THE FREIGHT SECTION RENDERS AT ALL. Import mode gets the real
  // QuoteShipment records; a domestic quote has no shipments and gets no
  // section. Defaulted to domestic so a caller that omits it shows nothing
  // rather than controls over an empty list.
  pricingMode?: PricingMode;
  shipments?: QuoteShipment[];
  // The quote's supplier currency, inherited by any shipment created here.
  // Empty means none recorded, which disables Add rather than hiding it.
  supplierCurrency?: string;
  // The engine's per-line results, so the domestic margin summary can be built
  // from validated numbers instead of the placeholder row fields. Absent means
  // "not priced yet", which the margin section reports rather than zeroing.
  livePricing?: Map<number, LivePricedLine>;
  // False for a read-only viewer: every create/edit/delete control is gated
  // on this, the same way the quote screen gates its other mutating controls.
  canEdit?: boolean;
  onChangeShipments?: (next: QuoteShipment[]) => void;
  // app review P2-13: on a narrow screen the panel sits BELOW the quote, full
  // width, and collapses to a "Show analysis" bar instead of a side rail.
  stacked?: boolean;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({
    sourcing: true, freight: true, fx: true, margin: true,
  });
  const toggle = (k: string) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // Freight is a section about SHIPMENTS, which exist on import quotes only.
  const showFreight = pricingMode === "import" && !!onChangeShipments;

  if (collapsed && stacked) {
    return (
      <button
        data-testid="side-panel-toggle"
        onClick={onToggle}
        style={{
          width: "100%", height: heights.control, marginTop: spacing[12],
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          backgroundColor: semanticColors.panelBg, color: semanticColors.textSecondary,
          fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
          cursor: "pointer",
        }}
      >
        Show analysis
      </button>
    );
  }

  if (collapsed) {
    return (
      <aside
        data-testid="side-panel-collapsed"
        style={{
          width: RAIL_WIDTH, height: "100%",
          borderLeft: `1px solid ${semanticColors.border}`,
          backgroundColor: semanticColors.panelBg,
          display: "flex", justifyContent: "center", paddingTop: spacing[8],
        }}
      >
        <button
          data-testid="side-panel-toggle"
          onClick={onToggle}
          title="Expand panel"
          style={{
            width: heights.iconButtonSm, height: heights.iconButtonSm, padding: 0, cursor: "pointer",
            background: "transparent", border: "none", color: semanticColors.textSecondary,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <ChevronsLeft size={iconSize.lg} strokeWidth={iconStroke.regular} />
        </button>
      </aside>
    );
  }

  return (
    <aside
      data-testid="side-panel"
      style={stacked ? {
        width: "100%", marginTop: spacing[12],
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface,
        display: "flex", flexDirection: "column",
      } : {
        width: PANEL_WIDTH,
        // Capped, so a 360px panel cannot leave the material table with
        // nothing. Inert at any width >= 376px -- see overlayMaxWidth.
        maxWidth: overlayMaxWidth,
        height: "100%",
        borderLeft: `1px solid ${semanticColors.border}`,
        backgroundColor: semanticColors.surface,
        boxShadow: shadows.sm,
        display: "flex", flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Top bar with collapse button */}
      <div className="flex items-center justify-between"
           style={{ padding: `${spacing[6]}px ${spacing[12]}px`, borderBottom: `1px solid ${semanticColors.border}`,
                    backgroundColor: semanticColors.panelBg, height: heights.control }}>
        <span style={{ ...HEADING, letterSpacing: typography.letterSpacing.widest }}>Quote Analysis</span>
        <button
          data-testid="side-panel-toggle"
          onClick={onToggle}
          title={stacked ? "Hide analysis" : "Collapse panel"}
          style={{
            width: stacked ? "auto" : heights.iconButtonSm, height: heights.iconButtonSm, padding: 0, cursor: "pointer",
            background: "transparent", border: "none", color: semanticColors.textSecondary,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: typography.fontSize.sm,
          }}
        >
          {stacked ? "Hide analysis" : <ChevronsRight size={iconSize.lg} strokeWidth={iconStroke.regular} />}
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>

        {/* THE HEADER GOES WITH THE SECTION. Shipments are import-mode only,
            and the domestic freight section that used to fill this slot was
            reading an invented bill -- see the note above ImportFreightSection.
            A header that opens onto nothing is worse than no header, so both
            are dropped together rather than leaving an empty accordion.

            Gated on the handler as well as the mode: without it the section
            renders controls that cannot write. */}
        {showFreight && (
          <>
            <SectionHeader id="freight" title="Freight" open={open.freight} onToggle={() => toggle("freight")} />
            {open.freight && (
              <ImportFreightSection
                shipments={shipments}
                supplierCurrency={supplierCurrency}
                canEdit={canEdit}
                onChangeShipments={onChangeShipments}
              />
            )}
          </>
        )}

        {/* Import only: the rates the lines carry, read-only. */}
        {pricingMode === "import" && (
          <>
            <SectionHeader id="fx" title="Conversion Rate" open={open.fx} onToggle={() => toggle("fx")} />
            {open.fx && <ConversionRateSection rows={rows} supplierCurrency={supplierCurrency} />}
          </>
        )}

        {/* Domestic only: the engine's figures (an import quote's margin is
            per line, in the table). */}
        {pricingMode === "domestic" && (
          <>
            <SectionHeader id="margin" title="Margin" open={open.margin} onToggle={() => toggle("margin")} />
            {open.margin && <MarginSection rows={rows} ctx={ctx} livePricing={livePricing} />}
          </>
        )}
      </div>

      {/* Fixed footer with 2 buttons */}
      <div style={{
        borderTop: `1px solid ${semanticColors.border}`, padding: spacing[12],
        display: "flex", flexDirection: "column", gap: spacing[8],
        backgroundColor: semanticColors.surface,
      }}>
        <button
          data-testid="btn-generate-pdf"
          className="btn-primary"
          onClick={onGeneratePdf}
          style={{
            width: "100%", height: heights.controlLg,
            backgroundColor: colors.indigo600, color: semanticColors.onAccent,
            border: "none", borderRadius: radius.base,
            fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
          }}
        >
          Generate Quote PDF
        </button>
        {/* "Send for Approval" WAS HERE AND IS GONE. It had no onClick, and
            there is no approval state, role or workflow behind it to wire one
            to: anixify.quote.state is draft/sent/won/lost with no approval
            step, and no Anixify group carries an approver right. A label that
            promises a workflow and does nothing is worse than a missing
            button -- the person believes they have sent it. Removed rather
            than disabled because the feature is not merely unbuilt but
            undesigned; see HANDOFF.md's deferred list. */}
      </div>
    </aside>
  );
}
